import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { config } from '../../config/environment';

/**
 * feat-163 — confirm a scorecard password reset with the emailed code
 * (ADR 0001 §5, AC3).
 *
 * Enumeration protection does NOT extend past this point (AC3): the user is
 * holding a code, so errors here are specific and honest. The one exception is
 * ratified in ADR §4.4 — a nonexistent account and a wrong code return the same
 * `invalid_code`, because "enumeration no longer applies" licenses
 * distinguishing CODE state, never ACCOUNT existence. The backend collapses
 * that distinction; this component must not try to recover it.
 *
 * Email carry-over mirrors PasswordChangePage.tsx:32-39 (router state, then
 * `?email=`), with one deliberate divergence: that component hard-redirects to
 * /scorecard-login when its inputs are missing (:49-53). This one must not
 * (ADR §5) — see `emailLocked` below.
 */

const confirmForgotPasswordUrl = (): string => {
  const scheme =
    config.environment === 'fake' || config.environment === 'local'
      ? 'http'
      : 'https';
  return `${scheme}://${config.restaurantBackendHost}/api/cognito-confirm-forgot-password`;
};

/**
 * `error_code` → user copy (ADR §4.4-R, §5.5). Keyed on the machine
 * discriminator, never on the prose in `error`, so backend copy edits can't
 * silently break this mapping.
 *
 * THIS TABLE IS PART OF THE ANTI-ENUM SURFACE, not merely a presentation of it.
 * A leak-free API rendered by a leaky client is a leaky feature: if this file
 * gives any outcome its own distinguishable message, it defeats a server-side
 * collapse completely, and no backend test can see it. Assume every entry you
 * add here is a potential oracle until you can say which of the two arguments
 * below protects it.
 *
 * The boundary is whether the code has VALIDATED — not whether it was entered.
 * Entering a code proves nothing; a code *validating* proves the actor owns the
 * mailbox. Everything reachable BEFORE validation is one bucket:
 *
 *   invalid_code  ← CodeMismatch + UserNotFound + ExpiredCode + NotAuthorized
 *
 * Two values were retired into that bucket, both for the same reason — they
 * asserted a semantics Cognito does not implement:
 *   • `code_expired` leaked existence in ONE deterministic request (a real
 *     account never issued a code returns "expired"; a nonexistent one returns
 *     "wrong" — and the pool's masking simply inverts the split, so no mapping
 *     of UserNotFound was safe: the attacker picks the scenario).
 *   • `session_invalid` described a session that ConfirmForgotPassword takes no
 *     parameter for; it was inherited from the change-password flow. Backend now
 *     buckets NotAuthorized here and tags it, so a config fault alarms instead
 *     of telling a user to retry forever.
 *
 * ⚠️ The two remaining distinct buckets are safe for DIFFERENT reasons. Do not
 * tidy them into one rule — the difference is the whole point:
 *
 *   • `invalid_password` — safe STRUCTURALLY. It is reachable only after the
 *     code validates, so the actor already owns the mailbox and there is no
 *     existence left to leak. Measured, not assumed (architect, unmasked client:
 *     a bogus code + a policy-violating password returns UserNotFound for a
 *     nonexistent account and ExpiredCode for a real one — neither returns
 *     InvalidPassword, proving Cognito checks the code FIRST). This cannot rot
 *     without Cognito reordering its own validation.
 *   • `throttled` — safe only EMPIRICALLY. It fires BEFORE validation, so the
 *     rule above does not protect it; it is distinct solely because QA measured
 *     it as uniform (identical threshold, counts and sequence for real and
 *     nonexistent accounts — first throttle at call #6 for both, §8.13). If AWS
 *     ever short-circuits confirm-throttling for unknown users, this line
 *     becomes an oracle silently, with nothing in our tests to catch it. If that
 *     is ever suspected: delete its line — `copyFor` falls back to invalid_code,
 *     and that deletion is the entire change.
 */
const ERROR_COPY: Record<string, string> = {
  invalid_code: "That code isn't valid or has expired. Request a new one.",
  invalid_password: 'Password does not meet requirements.',
  throttled: 'Too many attempts. Try again in a few minutes.',
};

/**
 * Unknown or absent `error_code` renders the invalid_code copy — never generic
 * copy, and never the backend's prose. An unmapped value must not be visually
 * distinguishable from a wrong code: a "something went wrong" fallback would
 * hand back the oracle the collapse just removed, by letting any future backend
 * error_code surface as its own distinct message.
 *
 * This also makes deploy skew safe in the direction it actually happens. FE and
 * backend ship separately, so for a real window this client WILL be talking to a
 * backend that still emits the retired vocabulary. Retired values map to nothing
 * here, so they land on invalid_code — the client refuses to resurrect a
 * distinction even when a stale server offers it.
 */
const copyFor = (errorCode?: string): string =>
  ERROR_COPY[errorCode ?? ''] ?? ERROR_COPY.invalid_code;

const ResetPasswordPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Router state first, then ?email= — the PasswordChangePage.tsx:32-39
  // precedence. Read once at mount: `location` does not change under this
  // component, so an effect would only add a redundant render.
  const [email, setEmail] = useState(() => {
    const stateEmail = (location.state as { email?: string } | null)?.email;
    if (stateEmail) return stateEmail;
    return new URLSearchParams(location.search).get('email') || '';
  });

  // Whether the email arrived with the user. When it did, it is read-only (it
  // is the address the code was sent to — letting it be edited would silently
  // guarantee a failed confirm). When it did not, the field must be editable:
  // ConfirmForgotPassword needs an email, so a locked-and-empty field would be
  // a dead page, and ADR §5 forbids the hard-redirect that PasswordChangePage
  // uses. A user who deep-links, refreshes, or comes back from their mail
  // client an hour later can still finish the reset by typing it.
  const [emailLocked] = useState(() => email !== '');

  const requestMessage = (location.state as { message?: string } | null)
    ?.message;

  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Email is required');
      return;
    }

    if (!code) {
      setError('Reset code is required');
      return;
    }

    // Password rules are PasswordChangePage.tsx:71-84 verbatim (ADR §5). The
    // live dev pool policy is min 6, so min 8 is stricter than the pool and can
    // never itself cause a Cognito rejection. Do not relax to 6; prod's policy
    // is unread, and if it turns out stricter than 8 the backend's
    // invalid_password is the backstop that surfaces it.
    if (!newPassword) {
      setError('New password is required');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(confirmForgotPasswordUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, new_password: newPassword }),
      });

      const data = await response.json();

      if (data.success) {
        // No auto-login (AC3). The success copy rides router state to
        // /scorecard-login, which renders it.
        navigate('/scorecard-login', {
          state: {
            message:
              'Your password has been reset. Sign in with your new password.',
          },
        });
        return;
      }

      // The backend's prose is rendered for exactly one code: invalid_password,
      // where it carries the pool's policy hint. Prod's password policy is
      // unread (ADR §6), so if it is stricter than our min-8 this hint is the
      // only way a user holding a valid code learns what is actually required —
      // otherwise they are rejected at the worst possible moment with no way
      // forward. Safe precisely because invalid_password is reachable only
      // after the code has validated, which proves the actor owns the mailbox;
      // there is no existence left to leak to them. Every other code renders
      // our copy and never the server's.
      setError(
        data.error_code === 'invalid_password' && data.error
          ? data.error
          : copyFor(data.error_code)
      );
    } catch {
      setError(
        "We couldn't reach the server. Check your connection and try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Bespoke AI</h1>
          <h2 className="text-xl text-gray-600 mb-8">Enter your reset code</h2>
        </div>
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {requestMessage && (
            <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
              <p className="text-sm">{requestMessage}</p>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                <p className="text-sm">{error}</p>
              </div>
            )}

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  disabled={emailLocked}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 sm:text-sm ${
                    emailLocked
                      ? 'bg-gray-50 text-gray-500'
                      : 'focus:outline-none focus:ring-indigo-500 focus:border-indigo-500'
                  }`}
                  placeholder="Enter the email you requested the code for"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="code"
                className="block text-sm font-medium text-gray-700"
              >
                Reset code
              </label>
              <div className="mt-1">
                <input
                  id="code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter the code from your email"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="new-password"
                className="block text-sm font-medium text-gray-700"
              >
                New Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="new-password"
                  name="new-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter your new password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Must be at least 8 characters long
              </p>
            </div>

            <div>
              <label
                htmlFor="confirm-password"
                className="block text-sm font-medium text-gray-700"
              >
                Confirm New Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Confirm your new password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={
                    showConfirmPassword ? 'Hide password' : 'Show password'
                  }
                >
                  {showConfirmPassword ? (
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Resetting...' : 'Reset password'}
              </button>
            </div>

            {/*
              Rendered unconditionally, for every user in every state. It is not
              a branch and carries no signal — but it is the only exit for the
              cases we cannot detect from here: an expired code, a request whose
              5xx we navigated through anyway (ADR §4.1-N), and the §3 black hole
              where the backend swallowed InvalidParameterException and no code
              was ever sent. Until the prod email_verified backfill lands (§7),
              that last case is the COMMON path, not the rare one.
            */}
            <div className="text-center">
              <button
                type="button"
                onClick={() =>
                  navigate('/forgot-password', { state: { email } })
                }
                className="text-sm text-indigo-600 hover:text-indigo-500"
              >
                Didn't get a code? Request a new one
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
